import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';

import objectiveService from './objectiveService';

import type { UpdateKeyResultProgressParams } from './objectiveService';
import type { PaginatedResponse, UserFilterParams } from '@/types/api';
import type {
    CreateObjectiveCommentRequest,
    CreateObjectiveRequest,
    EmployeesWithoutObjectivesParams,
    EmployeeWithoutObjective,
    Objective,
    ObjectiveComment,
    ObjectiveFilteredEmployee,
    ObjectiveOrDraft,
    ObjectiveParticipantAttribute,
    ObjectiveWindow,
    PillarSummary,
    ResolvedParticipantAttributes,
    UpdateObjectiveRequest,
} from '@/types/objectives';

import { queryClient } from '@/AppWithProviders';
import { ObjectiveMutationKey, ObjectiveQueryKey, PillarQueryKey } from '@/constants/QueryKeys';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';

type UpdateObjectiveParams = { id: string; request: UpdateObjectiveRequest };

/**
 * Refreshes every company-wide pillar summary, the list and each pillar: they count objectives and
 * echo the pillar's name, icon and status and the period's window.
 */
export const invalidatePillarSummaries = () => {
    queryClient.invalidateQueries({ queryKey: [ObjectiveQueryKey.COMPANY_PILLAR_SUMMARIES] });
    queryClient.invalidateQueries({ queryKey: [ObjectiveQueryKey.COMPANY_PILLAR_SUMMARY] });
};

const invalidateObjectiveLists = () => {
    queryClient.invalidateQueries({ queryKey: [ObjectiveQueryKey.OBJECTIVES] });
    invalidatePillarSummaries();
    // Aligning an objective with a pillar, or with several, moves the counts shown beside the
    // pillars on Vision and values.
    queryClient.invalidateQueries({ queryKey: [PillarQueryKey.OBJECTIVE_ALIGNMENT] });
    // Creating, editing or deleting an objective moves an employee in or out of the
    // "employees without an objective" list.
    queryClient.invalidateQueries({ queryKey: [ObjectiveQueryKey.EMPLOYEES_WITHOUT_OBJECTIVES] });
};

export const useGetObjectives = (objectiveWindow?: ObjectiveWindow, enabled = true) => {
    return useQuery<Objective[]>({
        queryKey: [ObjectiveQueryKey.OBJECTIVES, objectiveWindow],
        queryFn: () => objectiveService.getObjectives(objectiveWindow),
        enabled,
    });
};

/**
 * The objectives lists that show drafts -- the module's main page and the employee portal: the
 * published objectives in the window, then the drafts the caller sees, read within the same reach
 * as a published objective. Every other screen reads `useGetObjectives`, which never carries a draft.
 */
export const useGetObjectivesWithDrafts = (objectiveWindow?: ObjectiveWindow, enabled = true) => {
    return useQuery<ObjectiveOrDraft[]>({
        queryKey: [ObjectiveQueryKey.OBJECTIVES, objectiveWindow, 'withDrafts'],
        queryFn: () => objectiveService.getObjectivesWithDrafts(objectiveWindow),
        enabled,
    });
};

export const useGetObjectiveById = (id: string) => {
    return useQuery<ObjectiveOrDraft>({
        queryKey: [ObjectiveQueryKey.OBJECTIVE, id],
        queryFn: () => objectiveService.getObjectiveById(id),
        enabled: !!id,
    });
};

export const useCreateObjective = () => {
    return useMutation<ObjectiveOrDraft, Error, CreateObjectiveRequest>({
        mutationKey: [ObjectiveMutationKey.CREATE_OBJECTIVE],
        mutationFn: (request) => objectiveService.createObjective(request),
        onSuccess: invalidateObjectiveLists,
    });
};

export const useUpdateObjective = () => {
    return useMutation<ObjectiveOrDraft, Error, UpdateObjectiveParams>({
        mutationKey: [ObjectiveMutationKey.UPDATE_OBJECTIVE],
        mutationFn: ({ id, request }) => objectiveService.updateObjective(id, request),
        onSuccess: (savedObjective, { id }) => {
            // The saved objective replaces the cached one before anyone navigates: a draft just
            // published must not reach its detail page still reading as a draft while it reloads.
            queryClient.setQueryData([ObjectiveQueryKey.OBJECTIVE, id], savedObjective);
            invalidateObjectiveLists();
            queryClient.invalidateQueries({ queryKey: [ObjectiveQueryKey.OBJECTIVE, id] });
        },
    });
};

export const useDeleteObjective = () => {
    return useMutation<ObjectiveOrDraft, Error, string>({
        mutationKey: [ObjectiveMutationKey.DELETE_OBJECTIVE],
        mutationFn: (id) => objectiveService.deleteObjective(id),
        onSuccess: invalidateObjectiveLists,
    });
};

export const useUpdateKeyResultProgress = () => {
    return useMutation<Objective, Error, UpdateKeyResultProgressParams>({
        mutationKey: [ObjectiveMutationKey.UPDATE_KEY_RESULT_PROGRESS],
        mutationFn: (params) => objectiveService.updateKeyResultProgress(params),
        onSuccess: (_, { objectiveId }) => {
            invalidateObjectiveLists();
            queryClient.invalidateQueries({ queryKey: [ObjectiveQueryKey.OBJECTIVE, objectiveId] });
            // The server journals the move as an entry of the discussion.
            queryClient.invalidateQueries({
                queryKey: [ObjectiveQueryKey.OBJECTIVE_COMMENTS, objectiveId],
            });
        },
    });
};

/**
 * How far each pillar has come across the company, whoever holds the objectives: the only reading
 * of the pillars, whatever the screen. Readable by every employee, with no objectives permission.
 */
export const useGetCompanyPillarSummaries = (objectiveWindow?: ObjectiveWindow, enabled = true) => {
    return useQuery<PillarSummary[]>({
        queryKey: [ObjectiveQueryKey.COMPANY_PILLAR_SUMMARIES, objectiveWindow],
        queryFn: () => objectiveService.getCompanyPillarSummaries(objectiveWindow),
        enabled,
    });
};

/** One pillar across the whole company, as its tile reads it under "Organization". */
export const useGetCompanyPillarSummary = (
    pillarId: string,
    objectiveWindow?: ObjectiveWindow,
    enabled = true,
) => {
    return useQuery<PillarSummary>({
        queryKey: [ObjectiveQueryKey.COMPANY_PILLAR_SUMMARY, pillarId, objectiveWindow],
        queryFn: () => objectiveService.getCompanyPillarSummary(pillarId, objectiveWindow),
        enabled: enabled && !!pillarId,
    });
};

/**
 * The paginated, server-sorted list of employees with no objective in `params`' cycle window.
 * Idle until that window is known, because the cycle options resolve through their own query and a
 * window-less first fetch would answer for every period at once. `enabled` is how a caller says a
 * window-less query is the one it means -- the server counts every objective on two open bounds --
 * rather than one it has not resolved yet.
 */
export const useGetEmployeesWithoutObjectives = (
    params: EmployeesWithoutObjectivesParams,
    options?: { enabled?: boolean },
) => {
    return useQuery<PaginatedResponse<EmployeeWithoutObjective>>({
        queryKey: [ObjectiveQueryKey.EMPLOYEES_WITHOUT_OBJECTIVES, params],
        queryFn: () => objectiveService.getEmployeesWithoutObjectives(params),
        enabled: options?.enabled ?? (!!params.from && !!params.to),
    });
};

/**
 * Who the chosen attribute values bring in today. Idle without an attribute. On an objective being
 * edited, `objectiveId` resolves the attributes it already has within the reach they were added
 * under, for anyone who may edit it; a new attribute needs the right to add one (the server answers
 * 403 otherwise). While a new set is on its way the previous answer stays on screen as a
 * placeholder: `isPlaceholderData` tells it apart from an answer to the current set, which is the
 * only one a decision may be drawn from.
 */
export const useResolveParticipantAttributes = ({
    attributes,
    objectiveId,
    enabled,
}: {
    attributes: ObjectiveParticipantAttribute[];
    objectiveId?: string;
    enabled: boolean;
}) => {
    return useQuery<ResolvedParticipantAttributes>({
        queryKey: [ObjectiveQueryKey.PARTICIPANT_ATTRIBUTES_RESOLUTION, objectiveId, attributes],
        queryFn: () => objectiveService.resolveParticipantAttributes(attributes, objectiveId),
        enabled: enabled && attributes.length > 0,
        placeholderData: keepPreviousData,
    });
};

export const useGetObjectiveFilteredEmployees = (params: UserFilterParams, enabled = true) => {
    return useQuery<ObjectiveFilteredEmployee[]>({
        queryKey: [ObjectiveQueryKey.FILTERED_EMPLOYEES, params],
        queryFn: () => objectiveService.getFilteredEmployees(params),
        enabled,
        placeholderData: keepPreviousData,
    });
};

export const useGetChoosableTeamManagerIds = ({ enabled }: { enabled: boolean }) => {
    return useQuery<string[]>({
        queryKey: [ObjectiveQueryKey.CHOOSABLE_TEAM_MANAGERS],
        queryFn: objectiveService.getChoosableTeamManagerIds,
        enabled,
    });
};

export const useGetObjectiveComments = (objectiveId: string) => {
    // The server words each edit in the reader's language, from the Accept-Language header the
    // client sends: the thread is refetched when that language changes, not merely re-rendered.
    const language = useSelectedAppLanguage();

    return useQuery<ObjectiveComment[]>({
        queryKey: [ObjectiveQueryKey.OBJECTIVE_COMMENTS, objectiveId, language],
        queryFn: () => objectiveService.getObjectiveComments(objectiveId),
        enabled: !!objectiveId,
    });
};

type CreateObjectiveCommentParams = { objectiveId: string; request: CreateObjectiveCommentRequest };

export const useCreateObjectiveComment = () => {
    return useMutation<ObjectiveComment, Error, CreateObjectiveCommentParams>({
        mutationKey: [ObjectiveMutationKey.CREATE_OBJECTIVE_COMMENT],
        mutationFn: ({ objectiveId, request }) =>
            objectiveService.createObjectiveComment(objectiveId, request),
        onSuccess: (_, { objectiveId }) => {
            queryClient.invalidateQueries({
                queryKey: [ObjectiveQueryKey.OBJECTIVE_COMMENTS, objectiveId],
            });
        },
    });
};
